import React, { useState } from "react";
import { useAuth } from "../Utilities/AuthContext";
import { Navigate } from "react-router-dom";
import PoweredBy from "../ReusableComponents/PoweredBy";

const OwnerLogin = () => {
  const { isOwner, isInitialized } = useAuth();
  const [mode, setMode] = useState("signin"); // signin, signup
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [status, setStatus] = useState("idle"); // idle, sending, sent, error
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  if (!isInitialized) return null;
  if (isOwner) return <Navigate to="/home" replace />;

  const resetForm = () => {
    setStatus("idle");
    setErrorMessage("");
    setSuccessMessage("");
    setEmail("");
    setFirstName("");
    setLastName("");
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setErrorMessage("Please enter your email address");
      setStatus("error");
      return;
    }

    setStatus("sending");
    setErrorMessage("");

    try {
      const response = await fetch("/api/auth/sendMagicLink", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (response.ok) {
        setSuccessMessage(`We sent a login link to ${email}. Click the link in the email to sign in.`);
        setStatus("sent");
      } else {
        setErrorMessage("Something went wrong. Please try again.");
        setStatus("error");
      }
    } catch {
      setErrorMessage("Unable to connect. Please check your internet and try again.");
      setStatus("error");
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setErrorMessage("Please fill in all fields");
      setStatus("error");
      return;
    }

    setStatus("sending");
    setErrorMessage("");

    try {
      const response = await fetch("/api/auth/createAccount", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(data.message || "Account created! Check your email for a sign-in link.");
        setStatus("sent");
      } else if (response.status === 409) {
        setErrorMessage(data.error || "An account with this email already exists. Try signing in.");
        setStatus("error");
      } else {
        setErrorMessage(data.error || "Failed to create account. Please try again.");
        setStatus("error");
      }
    } catch {
      setErrorMessage("Unable to connect. Please check your internet and try again.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-dark via-primary to-primary-light flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white tracking-tight">GolfPools</h1>
          <p className="text-green-200 mt-2 text-lg">
            {mode === "signin" ? "Pool Owner Sign In" : "Create Your Account"}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {status === "sent" ? (
            /* Success State */
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Check Your Email</h2>
              <p className="text-gray-600 mb-6">{successMessage}</p>
              <p className="text-sm text-gray-500 mb-4">The link expires in 15 minutes.</p>
              <button
                onClick={resetForm}
                className="text-primary hover:text-primary-hover font-medium text-sm"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              {/* Tab Switcher */}
              <div className="flex mb-6 bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => { setMode("signin"); setErrorMessage(""); }}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                    mode === "signin"
                      ? "bg-white text-primary shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => { setMode("signup"); setErrorMessage(""); }}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                    mode === "signup"
                      ? "bg-white text-primary shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Create Account
                </button>
              </div>

              {mode === "signin" ? (
                /* Sign In Form */
                <form onSubmit={handleSignIn}>
                  <p className="text-gray-500 text-center mb-6 text-sm">Enter your email to receive a sign-in link</p>
                  <div className="mb-6">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setStatus("idle"); setErrorMessage(""); }}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 placeholder-gray-400"
                      disabled={status === "sending"}
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                  {status === "error" && errorMessage && (
                    <p className="mb-4 text-sm text-red-600 text-center">{errorMessage}</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  >
                    {status === "sending" ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sending...
                      </span>
                    ) : "Send Me a Login Link"}
                  </button>
                </form>
              ) : (
                /* Create Account Form */
                <form onSubmit={handleCreateAccount}>
                  <p className="text-gray-500 text-center mb-6 text-sm">Set up your account to start managing pools</p>
                  <div className="flex gap-3 mb-4">
                    <div className="flex-1">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                      <input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => { setFirstName(e.target.value); setStatus("idle"); setErrorMessage(""); }}
                        placeholder="John"
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 placeholder-gray-400"
                        disabled={status === "sending"}
                        autoFocus
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                      <input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => { setLastName(e.target.value); setStatus("idle"); setErrorMessage(""); }}
                        placeholder="Daly"
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 placeholder-gray-400"
                        disabled={status === "sending"}
                      />
                    </div>
                  </div>
                  <div className="mb-6">
                    <label htmlFor="signupEmail" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      id="signupEmail"
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setStatus("idle"); setErrorMessage(""); }}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 placeholder-gray-400"
                      disabled={status === "sending"}
                      autoComplete="email"
                    />
                  </div>
                  {status === "error" && errorMessage && (
                    <p className="mb-4 text-sm text-red-600 text-center">{errorMessage}</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  >
                    {status === "sending" ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Creating...
                      </span>
                    ) : "Create Account"}
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-green-200/70 text-sm mt-8">
          No password needed. We'll email you a secure link.
        </p>
        </div>
      </div>
      <PoweredBy variant="light" />
    </div>
  );
};

export default OwnerLogin;
